<template>
  <div class="settings-container">
    <!-- Mobile settings header with hamburger menu -->
    <div class="settings-mobile-header">
      <animated-hamburger
        :is-showing="showMobileMenu"
        @clicked="toggleMobileMenu"
      />
      <div class="settings-mobile-title">{{ currentSettingsTitle }}</div>
    </div>

    <!-- Settings sidebar - visible on desktop and as overlay on mobile -->
    <div
      class="settings-sidebar"
      :class="{ 'settings-sidebar-mobile-open': showMobileMenu }"
    >
      <b-nav
        vertical
        v-for="category in navMenu"
        :key="category.title"
        class="settings-sidebar-category"
      >
        <div class="settings-nav-cat-header">
          {{ $t(category.title).toUpperCase() }}
        </div>
        <localized-link
          v-for="link in category.links.filter((l) => l.visible)"
          :key="link.title"
          :to="link.uri"
          active-class="settingsNavItemActive"
          :class="{
            'settings-nav-item ': true,
            'disabled-settings-nav-item': !link.enabled,
          }"
          @click.native="closeMobileMenuIfOpen"
        >
          <b-nav-item class="settings-menu-item">
            {{ $t(link.title) }}
          </b-nav-item>
        </localized-link>
      </b-nav>
    </div>

    <!-- Settings content window -->
    <div
      class="settings-window-container"
      :class="{
        'settings-window-mobile': isMobileView,
        'banner-visible': showSetupBanner,
      }"
    >
      <!-- Setup notification header -->
      <div v-if="showSetupBanner" class="setup-notification-header">
        <div class="setup-notification-content">
          <i class="fi-info-circle mr-2"></i>
          {{ $t("setup.navbar_header") }}
          <router-link to="/en/setup" class="setup-link">
            {{ $t("setup.navbar_header_link") }}
          </router-link>
        </div>
      </div>
      <router-view />
    </div>

    <!-- Backdrop overlay when mobile menu is open -->
    <div
      v-if="showMobileMenu && isMobileView"
      class="settings-mobile-backdrop"
      @click="showMobileMenu = false"
    ></div>
  </div>
</template>

<script>
import LocalizedLink from "../../components/LocalizedLink";
import { routeConfig } from "../../config/routeConfig";
import AnimatedHamburger from "../../components/widgets/AnimatedHamburger.vue";
import { mapGetters } from "vuex";

export default {
  components: {
    LocalizedLink,
    AnimatedHamburger,
  },

  data() {
    return {
      showMobileMenu: false,
      isMobileView: false,
    };
  },

  computed: {
    ...mapGetters(["getVendor"]),
    ...mapGetters("subscription", ["hasPOSAccess"]),

    showSetupBanner() {
      return this.getVendor.completed_setup === 3;
    },

    navMenu() {
      const posRequiredRoutes = ["inventory", "menuCategories"];

      return [
        {
          title: "settings.navigation.title",
          links: Object.entries(routeConfig.settings)
            .map(([key, config]) => ({
              title: config.title,
              uri: `/settings/${config.path}`,
              enabled: config.enabled,
              visible: config.visible,
              requiresPOS: posRequiredRoutes.includes(key),
            }))
            .filter((link) => {
              // Filter out POS-required routes if user doesn't have access
              if (link.requiresPOS && !this.hasPOSAccess) {
                return false;
              }
              return true;
            })
            .map((link) => ({
              title: link.title,
              uri: link.uri,
              enabled: link.enabled || (link.requiresPOS && this.hasPOSAccess),
              visible: link.visible || (link.requiresPOS && this.hasPOSAccess),
            })),
        },
      ];
    },

    currentSettingsTitle() {
      const currentPath = this.$route.path.split("/").pop();
      const currentSetting = Object.values(routeConfig.settings).find(
        (setting) => setting.path === currentPath
      );
      return currentSetting
        ? this.$t(currentSetting.title)
        : this.$t("settings.navigation.title");
    },
  },

  methods: {
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu;
    },

    closeMobileMenuIfOpen() {
      if (this.isMobileView) {
        this.showMobileMenu = false;
      }
    },

    checkMobileView() {
      this.isMobileView = window.innerWidth < 768;
    },
  },

  mounted() {
    this.checkMobileView();
    window.addEventListener("resize", this.checkMobileView);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobileView);
  },
};
</script>

<style lang="scss">
@import "@/style/_variables.scss";

// Mobile settings header styles
.settings-mobile-header {
  display: none;
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 50px;
  background-color: #6096d1;
  padding: 0 1rem;
  z-index: 10;
  align-items: center;
  color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .settings-mobile-title {
    flex: 1;
    text-align: center;
    font-weight: 500;
    font-size: 1.1rem;
    margin-right: 40px; // Offset to balance the hamburger on the left
  }

  #menuToggle {
    span {
      background: #fff;
    }
  }
}

// Settings navigation category header
.settings-nav-cat-header {
  padding: 0.5rem;
  width: 100%;
  background-color: #6096d1;
  color: #fff;
  font-weight: 500;
  border-radius: 4px 4px 0 0;
}

// Main settings container
.settings-container {
  margin-right: 0;
  height: 100%;
  width: 100%;
  top: 0px;
  left: 0px;
  position: absolute;
  overflow-y: auto;
}

// Settings window styles
.settings-window-container {
  position: absolute;
  top: 0px;
  bottom: 0;
  left: 200px;
  right: 0;
  overflow-y: auto;
  background: $main_theme_bg_color;
}

// Settings sidebar styles
.settings-sidebar {
  position: fixed;
  width: 200px;
  padding: 0px;
  background: $light_grey;
  height: 100%;
  border-right: 1px solid $grey;
}

.settings-sidebar a {
  color: $main_theme_fg_color;
  transition: all 0.2s ease;
}

.settings-sidebar-category a:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.settings-window {
  position: fixed;
  right: 0px;
  bottom: 0px;
  left: 254px;
  top: 54px;
  width: auto;
  padding-right: 0px;
  padding-left: 0px;
}

.settingsNavItemActive {
  background-color: rgba(0, 123, 255, 0.1);
  color: #444 !important;
  border-left: 3px solid #007bff;

  .settings-menu-item {
    color: #444 !important;
    font-weight: 500;
    padding-left: 0.55rem;
  }
}

.background-image {
  position: fixed;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  opacity: 0;
}

.settings-menu-item {
  pointer-events: none;
  padding: 0.5rem 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.disabled-settings-nav-item {
  opacity: 0.35;
  pointer-events: none;
}

// Mobile backdrop overlay
.settings-mobile-backdrop {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 5;
}

// Media query for mobile devices
@media screen and (max-width: 767px) {
  .settings-mobile-header {
    display: flex;
  }

  .settings-sidebar {
    position: fixed;
    top: 50px; // Below the mobile header
    left: -200px; // Hidden by default
    z-index: 10;
    transition: left 0.3s ease;
    height: calc(100% - 50px);
  }

  .settings-sidebar-mobile-open {
    left: 0; // Show when open
  }

  .settings-window-container {
    left: 0;
    top: 50px; // Below the mobile header
    height: calc(100% - 50px);
  }

  .settings-mobile-backdrop {
    display: block;
  }
}

// Setup notification header styles
.setup-notification-header {
  position: sticky;
  top: 0;
  z-index: 5; // above card contents but below mobile header (z10)
  background-color: #fff7dd;
  border: 1px solid #ffe9a8;
  padding: 10px 16px;

  .setup-notification-content {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #856404;

    .setup-link {
      margin-left: 5px;
      color: #0056b3;
      font-weight: 600;
    }
  }
}

.settings-window-container.banner-visible {
  .booking-packages-view {
    margin-top: 50px;
  }

  .settings-container {
    margin-top: 50px;
  }

  .settings-window-container {
    margin-top: 50px;
  }
}
</style>
